import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Table from "components/DesignSystem/Table/V2/Table";
import { StartBookkeepingDateEmptyScreen } from "components/GeneralLedger/GeneralLedgerEmptyScreen";
import { ArrowRight } from "components/icons/ArrowRight";
import { Redirect } from "components/icons/Redirect";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReportsType } from "pages/Books/Reports/Reports";
import { Fragment, useMemo, useState } from "react";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { redirectToCategories } from "utils/redirectToCategories";
import { stopPropagation } from "utils/stopPropagation";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import { ReportFilterType } from "../ReportFilter";
import { useViewByReportDataFormat } from "../hook/useViewByReportDataFormat";
import { AccountWithMonths } from "../utils/formatViewReportData";
import { COLUMN_IDS, COMPARE_PERIOD, VARIANCE } from "constants/reports";
import {
  VARIANCE_REPORT_MODE,
  VarianceReportModeType,
} from "constants/financialClosing";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { VarianceExplanationCell } from "./VarianceExplanationCell";
import { ReportSummaryBanner } from "./ReportSummaryBanner";
import { ComparisonReportData, VarianceExplanation } from "types/Models/report";
import { useGetVarianceExplanationListQuery } from "store/apis/reports";
import { VarianceReportSkeleton } from "./VarianceReportSkeleton";

type VarianceTableMeta = {
  reportType: ReportsType;
  filter: ReportFilterType;
  mode: VarianceReportModeType;
  regenerateToken: number;
  groupId: string;
  entityId: string;
  isAdmin: boolean;
  currentPeriodKey: string;
  comparisonPeriodKey: string;
  showVarianceAmount: boolean;
  showVariancePercentage: boolean;
  storedExplanationsByKey: Map<string, VarianceExplanation>;
};

const columnHelper = createColumnHelper<AccountWithMonths>();

const MOBILE_COLUMN_SIZING = { name: 150 };
const DESKTOP_COLUMN_SIZING = {};

const columns = [
  columnHelper.accessor("name", {
    header: "Category",
    size: 270,
    cell: ({ row, getValue, table }) => {
      const name = getValue();
      const { groupId, entityId, filter, isAdmin } = table.options
        .meta as VarianceTableMeta;

      return (
        <div
          style={{
            paddingLeft: `${row.depth * 1}rem`,
          }}
          className={classNames("t-flex t-items-center")}
        >
          <div
            className={classNames({
              "t-rotate-90 t-transform t-transition t-duration-300 t-ease-in-out":
                row.getIsExpanded(),
              "t-invisible": !row.getCanExpand(),
            })}
          >
            <ArrowRight stroke="1.5" color="currentColor" />
          </div>
          <div className="t-flex t-justify-end t-h-full t-items-center t-gap-3">
            {name}
            {row.depth > 0 && (
              <span className="group-hover:t-visible t-invisible">
                <ConditionalLink
                  to={redirectToCategories({
                    uuid: row.original.uuid,
                    groupId,
                    entityId,
                    filter,
                    isAdmin,
                  })}
                >
                  <Redirect size={16} />
                </ConditionalLink>
              </span>
            )}
          </div>
        </div>
      );
    },
  }),

  columnHelper.group({
    id: "Current period",
    header: () => <HeadCellContent title="Current period" align="right" />,
    columns: [
      columnHelper.accessor("uuid", {
        id: "current_period",
        size: 200,
        header: ({ table }) => {
          const { currentPeriodKey } = table.options.meta as VarianceTableMeta;
          return <HeadCellContent title={currentPeriodKey} align="right" />;
        },
        cell: ({ row, table }) => {
          const { currentPeriodKey } = table.options.meta as VarianceTableMeta;
          const amount =
            row.original.monthData?.[currentPeriodKey]?.summary_amount;

          if (amount === null)
            return <div className="t-flex t-justify-end t-ml-4">-</div>;

          return (
            <div className="t-flex t-justify-end t-ml-4">
              <AmountSuperScript amount={Number(amount)} />
            </div>
          );
        },
      }),
    ],
  }),

  columnHelper.group({
    id: "Comparison period",
    header: () => <HeadCellContent title="Comparison period" align="right" />,
    columns: [
      columnHelper.accessor("uuid", {
        id: "comparison_period",
        size: 200,
        header: ({ table }) => {
          const { comparisonPeriodKey } = table.options
            .meta as VarianceTableMeta;
          return <HeadCellContent title={comparisonPeriodKey} align="right" />;
        },
        cell: ({ row, table }) => {
          const { comparisonPeriodKey } = table.options
            .meta as VarianceTableMeta;
          const amount =
            row.original.monthData?.[comparisonPeriodKey]?.summary_amount;

          if (amount === null)
            return <div className="t-flex t-justify-end t-ml-4">-</div>;

          return (
            <div className="t-flex t-justify-end t-ml-4">
              <AmountSuperScript amount={Number(amount)} />
            </div>
          );
        },
      }),
    ],
  }),

  columnHelper.group({
    id: "Variance",
    header: ({ table }) => {
      const { showVarianceAmount, showVariancePercentage } = table.options
        .meta as VarianceTableMeta;
      if (!showVarianceAmount && !showVariancePercentage) return null;

      return (
        <HeadCellContent
          title="Flux"
          align={
            showVarianceAmount && showVariancePercentage ? "center" : "right"
          }
        />
      );
    },
    columns: [
      columnHelper.display({
        id: "variance_amount",
        header: ({ table }) => {
          const { showVarianceAmount } = table.options
            .meta as VarianceTableMeta;
          if (!showVarianceAmount) return null;
          return <HeadCellContent title="Amount ($)" align="right" />;
        },
        size: 130,
        maxSize: 130,
        cell: ({ row, table }) => {
          const { showVarianceAmount } = table.options
            .meta as VarianceTableMeta;
          if (!showVarianceAmount) return null;

          const variance_amount =
            row.original.monthData?.Variance?.summary_variance_amount;

          if (variance_amount === null)
            return <div className="t-flex t-justify-end">-</div>;

          return (
            <div className="t-flex t-justify-end">
              <AmountSuperScript amount={Number(variance_amount)} />
            </div>
          );
        },
      }),

      columnHelper.display({
        id: "variance_percentage",
        header: ({ table }) => {
          const { showVariancePercentage } = table.options
            .meta as VarianceTableMeta;
          if (!showVariancePercentage) return null;
          return <HeadCellContent title="Percentage (%)" align="right" />;
        },
        size: 130,
        maxSize: 130,
        cell: ({ row, table }) => {
          const { showVariancePercentage } = table.options
            .meta as VarianceTableMeta;
          if (!showVariancePercentage) return null;

          const variance_percentage =
            row.original.monthData?.Variance?.summary_variance_percentage;

          if (variance_percentage === null)
            return <div className="t-flex t-justify-end">-</div>;

          return (
            <div className={classNames("t-flex t-justify-end")}>
              {(variance_percentage ?? 0).toFixed(2)}%
            </div>
          );
        },
      }),
    ],
  }),

  columnHelper.display({
    id: COLUMN_IDS.EXPLANATION,
    header: () => (
      <div className="t-flex t-items-center t-gap-2">
        <span className="t-text-subtext-sm t-text-text-30">Explanation</span>
        <Chip size="xsmall" rounded isFixedFilter isRemovable={false}>
          <img
            src={AIGenerateButtonStar}
            alt="AIGenerateButtonStar"
            className="t-size-3.5"
          />
          AI
        </Chip>
      </div>
    ),
    size: 120,
    cell: ({ row, table }) => {
      const {
        reportType,
        filter,
        mode,
        regenerateToken,
        storedExplanationsByKey,
      } = table.options.meta as VarianceTableMeta;
      const rowKey = row.original.uuid;
      const isSyntheticRow = !row.original.identifier;
      const explanationKey = isSyntheticRow ? row.original.name : rowKey;

      return (
        <div onClick={stopPropagation}>
          <VarianceExplanationCell
            categoryUuid={isSyntheticRow ? null : rowKey}
            syntheticRowName={isSyntheticRow ? row.original.name : null}
            reportType={reportType}
            filter={filter}
            mode={mode}
            regenerateCount={regenerateToken}
            storedExplanation={storedExplanationsByKey.get(explanationKey)}
          />
        </div>
      );
    },
  }),
];

const DESKTOP_PINNING = { left: ["name"] };
// On phones a pinned name column fills the screen, so everything scrolls together instead.
const NO_PINNING = { left: [] };

export const VarianceComparisonReport = ({
  reportType,
  reportId,
  filter,
  mode,
  regenerateToken = 0,
}: {
  reportType: ReportsType;
  reportId?: string;
  filter: ReportFilterType;
  mode: VarianceReportModeType;
  regenerateToken?: number;
}) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { formatViewReportData, originalData, isFetching, isSuccess } =
    useViewByReportDataFormat({
      reportType,
      reportId,
      filter,
      isVarianceReport: true,
      useCachedReport: mode === VARIANCE_REPORT_MODE.VIEW,
      regenerateCount: regenerateToken,
    });
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { report_data = {} } = originalData || {};

  const reportDatabyView = useMemo(
    () => formatViewReportData,
    [isFetching, JSON.stringify(formatViewReportData)]
  );

  const { data: storedExplanationsData } = useGetVarianceExplanationListQuery(
    {
      groupId,
      entityId,
      reportType,
      startDate: filter.START_DATE || null,
      endDate: filter.END_DATE || null,
      accountingMethod: filter.ACCOUNTING_METHOD,
      comparisonReportType: filter.COMPARE_BY,
    },
    { skip: mode === VARIANCE_REPORT_MODE.GENERATE }
  );

  const storedExplanationsByKey = useMemo(() => {
    const map = new Map<string, VarianceExplanation>();
    if (!storedExplanationsData) return map;

    storedExplanationsData.explanations.forEach((explanation) => {
      const key =
        explanation.category_uuid ||
        explanation.synthetic_row_name ||
        explanation.category_name;
      if (key) map.set(key, explanation);
    });

    return map;
  }, [storedExplanationsData]);

  const storedOverview = useMemo(() => {
    if (!storedExplanationsData) return undefined;

    return storedExplanationsData.explanations.find(
      (explanation) => !explanation.category_uuid && !explanation.category_name
    );
  }, [storedExplanationsData]);

  const reportDataKeys = Object.keys(report_data).filter(
    (key) => key !== "Variance"
  );

  const currentPeriodKey =
    reportDataKeys.find(
      (key) =>
        (report_data as ComparisonReportData)[key]?.compare_period ===
        COMPARE_PERIOD.CURRENT
    ) ||
    reportDataKeys[0] ||
    "";
  const comparisonPeriodKey =
    reportDataKeys.find(
      (key) =>
        (report_data as ComparisonReportData)[key]?.compare_period ===
        COMPARE_PERIOD.PREVIOUS
    ) ||
    reportDataKeys[1] ||
    "";

  const showVarianceAmount = Boolean(
    filter.VARIANCE?.includes(VARIANCE.AMOUNT)
  );
  const showVariancePercentage = Boolean(
    filter.VARIANCE?.includes(VARIANCE.PERCENTAGE)
  );

  const meta: VarianceTableMeta = {
    reportType,
    filter,
    mode,
    regenerateToken,
    groupId,
    entityId,
    isAdmin,
    currentPeriodKey,
    comparisonPeriodKey,
    showVarianceAmount,
    showVariancePercentage,
    storedExplanationsByKey,
  };

  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: reportDatabyView,
    columns,
    meta,
    state: {
      expanded,
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
      columnSizing: isMobile ? MOBILE_COLUMN_SIZING : DESKTOP_COLUMN_SIZING,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.types,
    getRowId: (row) => row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const showBorderForSticky = (columnId: string) => columnId === "name";

  const firstVarianceColumnId = (() => {
    if (showVarianceAmount) return COLUMN_IDS.VARIANCE_AMOUNT;
    if (showVariancePercentage) return COLUMN_IDS.VARIANCE_PERCENTAGE;
    return null;
  })();

  const isBodyBorderColumn = (columnId: string) =>
    columnId === COLUMN_IDS.COMPARISON_PERIOD ||
    columnId === firstVarianceColumnId ||
    columnId === COLUMN_IDS.EXPLANATION;

  const showHeaderVerticalBorder = (columnId: string) =>
    (
      [COLUMN_IDS.CURRENT_PERIOD, COLUMN_IDS.COMPARISON_PERIOD] as string[]
    ).includes(columnId);

  return (
    <Async.Root
      {...{ isEmpty: false, isLoading: isFetching, isSuccess }}
      customLoader={<VarianceReportSkeleton />}
    >
      <Async.Empty>
        <StartBookkeepingDateEmptyScreen />
      </Async.Empty>
      <Async.Success>
        <div className="t-relative t-flex t-flex-col t-pb-20">
          <div className="t-mb-4 t-shrink-0">
            <ReportSummaryBanner
              reportType={reportType}
              filter={filter}
              mode={mode}
              regenerateCount={regenerateToken}
              period={currentPeriodKey}
              storedOverview={storedOverview}
            />
          </div>
          <Table.Container
            sticky
            className="t-w-full t-pb-24 scrollbar-visible"
          >
            <Table.Content className="t-w-full">
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => {
                  const isGroupHeader = headerGroup.depth === 0;

                  return (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const styles = getCommonPinningStyles(header.column);
                        const { boxShadow } = getPinnedShadowClass(
                          header.column
                        );
                        const isPinnedColumn = header.column.getIsPinned();
                        const isUngroupedLeafColumn = (
                          [COLUMN_IDS.EXPLANATION, "name"] as string[]
                        ).includes(header.column.id);
                        const hideHeaderContent =
                          isGroupHeader && isUngroupedLeafColumn;

                        return (
                          <Table.HeadCell
                            isUpperCase={false}
                            key={header.id}
                            style={{
                              ...styles,
                              minWidth: header.column.getSize(),
                            }}
                            colSpan={header.colSpan}
                            className={classNames(
                              "!t-text-subtext-sm",
                              boxShadow,
                              {
                                "!t-bg-surface-lighter-grey": isPinnedColumn,
                                "t-drop-shadow-border-shadow-neutral-0":
                                  showBorderForSticky(header.column.id),
                                "!t-z-[3]":
                                  showBorderForSticky(header.column.id) &&
                                  header.column.getIsPinned(),
                                "t-border-r t-border-neutral-0 ":
                                  showHeaderVerticalBorder(header.column.id) ||
                                  (!showHeaderVerticalBorder(
                                    header.column.id
                                  ) &&
                                    header.colSpan > 0),
                              }
                            )}
                          >
                            {!hideHeaderContent &&
                              flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </Table.HeadCell>
                        );
                      })}
                    </Table.HeadRow>
                  );
                })}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <Fragment key={row.id}>
                      <Table.Row
                        aria-label={row.original.name}
                        aria-disabled={!row.getCanExpand()}
                        aria-expanded={row.getIsExpanded()}
                        onClick={() => {
                          row.toggleExpanded();
                        }}
                        className={classNames(
                          "t-select-none t-px-3 t-text-body-sm t-group t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0",
                          {
                            "t-text-text-100": row.depth === 0,
                            "t-text-text-60": row.depth === 1,
                            "t-text-text-30": row.depth >= 2,
                            "t-cursor-pointer hover:t-bg-surface-lighter-grey":
                              row.getCanExpand(),
                          }
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const styles = getCommonPinningStyles(cell.column);
                          const { boxShadow } = getPinnedShadowClass(
                            cell.column
                          );

                          return (
                            <Table.Cell
                              key={cell.id}
                              style={{
                                ...styles,
                                minWidth: cell.column.getSize(),
                                ...(cell.column.id === "name"
                                  ? { width: "100px", minWidth: "100px" }
                                  : {}),
                              }}
                              className={classNames(boxShadow, {
                                "t-drop-shadow-border-shadow-neutral-0":
                                  showBorderForSticky(cell.column.id),
                                "!t-z-[1]":
                                  showBorderForSticky(cell.column.id) &&
                                  !cell.column.getIsPinned(),
                                "!t-z-[3]":
                                  showBorderForSticky(cell.column.id) &&
                                  cell.column.getIsPinned(),
                                "t-border-l t-border-neutral-0":
                                  isBodyBorderColumn(cell.column.id),
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </Table.Row>
                    </Fragment>
                  );
                })}
              </Table.Body>
            </Table.Content>
          </Table.Container>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
